﻿import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, BookOpen } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Button } from '@/components/ui/button';
import { sopService } from '@/services/sop.service';

interface SopWidgetProps {
  isAdminTier: boolean;
}

function ComplianceRing({ pct }: { pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
      <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3" className="text-muted/30" />
        <circle
          cx="18"
          cy="18"
          r="15.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray={`${(clamped / 100) * 97.4} 97.4`}
          strokeLinecap="round"
          className="text-teal-500 transition-all duration-500"
        />
      </svg>
      <span className="relative text-sm font-bold text-foreground">{clamped}%</span>
    </div>
  );
}

function AdminSop() {
  const { has } = usePermission();

  const statsQ = useQuery({
    queryKey: ['dashboard', 'sop', 'stats'],
    queryFn: () => sopService.stats(),
    enabled: has(PERMISSIONS.SOP_COMPLIANCE_VIEW),
  });

  if (statsQ.isLoading) {
    return (
      <Card className="shadow-card">
        <SectionHeader icon={<BookOpen />} title="SOP Compliance" />
        <CardContent className="p-4">
          <div className="h-24 animate-pulse rounded-xl bg-muted/50" />
        </CardContent>
      </Card>
    );
  }

  if (statsQ.isError) {
    return (
      <Card className="shadow-card">
        <SectionHeader icon={<BookOpen />} title="SOP Compliance" />
        <CardContent className="p-4">
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-muted-foreground">Failed to load</p>
            <Button size="sm" variant="outline" onClick={() => statsQ.refetch()}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const stats = statsQ.data;
  const pct = Math.round((stats?.overall_ack_rate ?? 0) * 100);
  const total = stats?.total_assignments ?? 0;
  const acknowledged = Math.round(total * (stats?.overall_ack_rate ?? 0));
  const pending = total - acknowledged;

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<BookOpen />}
        title="SOP Compliance"
        action={
          <Link to="/hrms/sop" className="text-xs font-normal text-primary hover:underline">
            Manage
          </Link>
        }
      />
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <ComplianceRing pct={pct} />
          <div className="space-y-1">
            <p className="text-sm font-semibold">
              {acknowledged} / {total} acknowledged
            </p>
            <p className="text-xs text-muted-foreground">{pending} pending acknowledgments</p>
            {stats?.overdue_count != null && stats.overdue_count > 0 && (
              <p className="text-xs text-amber-600">{stats.overdue_count} overdue</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function UserSop() {
  const { has } = usePermission();

  const myAssignmentsQ = useQuery({
    queryKey: ['dashboard', 'sop', 'my-assignments'],
    queryFn: () => sopService.listMyAssignments({ page: 1, limit: 5 }),
    enabled: has(PERMISSIONS.SOP_ACKNOWLEDGE),
  });

  const pendingItems = (myAssignmentsQ.data?.data ?? []).filter(
    (item) => !item.acknowledged_at,
  );

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<BookOpen />}
        title="SOPs to Acknowledge"
        action={
          <Link to="/sop" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="p-0">
        {myAssignmentsQ.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-muted/50" />
            ))}
          </div>
        ) : myAssignmentsQ.isError ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-muted-foreground">Failed to load</p>
            <Button size="sm" variant="outline" onClick={() => myAssignmentsQ.refetch()}>
              Retry
            </Button>
          </div>
        ) : pendingItems.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <BookOpen className="h-7 w-7 opacity-30" />
            <p className="text-sm text-muted-foreground">All SOPs acknowledged</p>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {pendingItems.map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{item.title}</p>
                  {item.category_name && (
                    <p className="text-xs text-muted-foreground">{item.category_name}</p>
                  )}
                </div>
                <Link
                  to="/sop"
                  className="shrink-0 text-xs font-medium text-primary hover:underline"
                >
                  Acknowledge
                </Link>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SopWidget({ isAdminTier }: SopWidgetProps) {
  if (isAdminTier) return <AdminSop />;
  return <UserSop />;
}
